import { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { useMenuTriggerState } from '@react-stately/menu';
import { ListCollection, useListState } from '@react-stately/list';
const CREATE_OPTION_VALUE = `GWC_OPTION_CREATE_OPTION_VALUE`;
const CREATABLE_OPTION = { value: CREATE_OPTION_VALUE };
function stubTrue() {
    return true;
}
function isCreatableOption(option) {
    return option === CREATABLE_OPTION;
}
export function useSelectState(props) {
    const { value, disabledKeys, options, multi, onChange, renderOption, makeKey, makeText, filter, creatable, closeOnSelect = !multi, openOnFocus = true, } = props;
    const selectionMode = multi ? 'multiple' : 'single';
    const patchedMakeKey = useCallback((option) => {
        if (isCreatableOption(option)) {
            return CREATE_OPTION_VALUE;
        }
        return makeKey(option);
    }, [makeKey]);
    const [inputValue, setInputValue] = useState(Array.isArray(value) ? '' : value ? makeText(value) : '');
    const finalOptions = useMemo(() => {
        if (creatable) {
            let items = [...options];
            if (creatable.position === 'end') {
                items.push(CREATABLE_OPTION);
            }
            else {
                items.unshift(CREATABLE_OPTION);
            }
            return items;
        }
        return options;
    }, [options, creatable]);
    const selectedKeys = useMemo(() => {
        if (!multi) {
            if (Array.isArray(value)) {
                throw new Error('For single select value has to be an object');
            }
            return value === undefined || value === null ? [] : [patchedMakeKey(value)];
        }
        if (Array.isArray(value))
            return value.map(patchedMakeKey);
        if (value !== undefined) {
            throw new Error('For multi select value has to be an array');
        }
        return [];
    }, [value, multi, patchedMakeKey]);
    function getSelectionDiff(next) {
        const prev = new Set(selectedKeys);
        for (const key of next) {
            if (!prev.has(key)) {
                return { key, on: true };
            }
        }
        for (const key of prev) {
            if (!next.has(key)) {
                return { key, on: false };
            }
        }
        return null;
    }
    function onSelectionChange(selection) {
        var _a, _b;
        if (selection === 'all') {
            console.warn("'all' selection is not supported");
            return;
        }
        const values = [];
        for (const key of selection) {
            const node = collection.getItem(key);
            values.push(node.value);
            if (node.key === CREATE_OPTION_VALUE) {
                (_a = creatable === null || creatable === void 0 ? void 0 : creatable.onCreate) === null || _a === void 0 ? void 0 : _a.call(creatable, inputValue);
                triggerState.close();
                return;
            }
        }
        onChange === null || onChange === void 0 ? void 0 : onChange(multi ? values : (_b = values[0]) !== null && _b !== void 0 ? _b : null);
        if (value === undefined) {
            console.warn("Input value doesn't set up for uncontrolled select");
        }
        if (triggerState.isOpen &&
            (typeof closeOnSelect === 'function'
                ? closeOnSelect(getSelectionDiff(selection))
                : closeOnSelect)) {
            triggerState.close();
        }
    }
    useEffect(() => {
        var _a;
        if (value !== undefined) {
            const newInputValue = multi ? '' : value ? (_a = makeText(value)) !== null && _a !== void 0 ? _a : '' : '';
            lastSelectedValue.current = newInputValue;
            if (newInputValue !== inputValue) {
                setInputValue(newInputValue);
            }
        }
    }, [value]);
    const { collection, selectionManager, disabledKeys: disabledKeysSet, } = useListState({
        selectionMode,
        items: finalOptions,
        selectedKeys,
        disabledKeys,
        onSelectionChange,
        children: (o) => isCreatableOption(o) && creatable
            ? creatable === null || creatable === void 0 ? void 0 : creatable.render(CREATE_OPTION_VALUE)
            : renderOption(o),
    });
    const lastSelectedValue = useRef(inputValue);
    const isCreatableOptionVisible = creatable === null || creatable === void 0 ? void 0 : creatable.isVisible;
    const filteredCollection = useMemo(() => filterCollection(collection, inputValue, filter !== null && filter !== void 0 ? filter : defaultFilter, isCreatableOptionVisible), [collection, inputValue, defaultFilter, isCreatableOptionVisible]);
    const collectionWithoutCreate = useMemo(() => getCollectionWithoutCreateOption(collection), [collection]);
    const visibleCollection = lastSelectedValue.current === inputValue ? collectionWithoutCreate : filteredCollection;
    const triggerState = useMenuTriggerState({
        ...props,
        onOpenChange: undefined,
        isOpen: undefined,
        defaultOpen: undefined,
    });
    // восстанавливаем инпут в последнее валидное значение
    function restoreInputValue() {
        var _a, _b;
        if (multi || !selectionManager.firstSelectedKey) {
            setInputValue('');
            return;
        }
        setInputValue((_b = makeText((_a = collection.getItem(selectionManager.firstSelectedKey)) === null || _a === void 0 ? void 0 : _a.value)) !== null && _b !== void 0 ? _b : '');
    }
    useEffect(() => {
        if (selectionManager.focusedKey &&
            !visibleCollection.getItem(selectionManager.focusedKey)) {
            // скидываем фокус, если фильтрация поломала навигацию и е можно было бы начать заново
            selectionManager.setFocusedKey(null);
        }
    });
    useEffect(() => {
        // чистим фокус при закрытии дропдауна
        if (selectionManager.focusedKey && !triggerState.isOpen) {
            selectionManager.setFocusedKey(null);
        }
    }, [triggerState.isOpen, selectionManager]);
    useEffect(() => {
        if (triggerState.isOpen || lastSelectedValue.current === inputValue)
            return;
        // открываем меню автоматом, если начали менять инпут поле последнего изменения селекта
        // например выбрали айтем, меню захлопнулось, начали менять текст в инпуте, меню снова открылось
        // для мультиселекта не актуально
        triggerState.open();
    }, [inputValue]);
    function revert() {
        restoreInputValue();
        triggerState.close();
    }
    function commit() {
        if (selectionManager.focusedKey) {
            selectionManager.select(selectionManager.focusedKey);
        }
        else {
            restoreInputValue();
        }
    }
    function setFocused(isFocused) {
        if (!isFocused) {
            revert();
        }
        else if (openOnFocus) {
            triggerState.open();
        }
        selectionManager.setFocused(isFocused);
    }
    return {
        ...triggerState,
        selectionManager,
        disabledKeys: disabledKeysSet,
        isFocused: selectionManager.isFocused,
        setFocused,
        collection: visibleCollection,
        originCollection: collection,
        inputValue,
        setInputValue,
        commit,
        revert,
        selectionMode,
    };
}
function defaultFilter(textValue, inputValue) {
    return textValue.toLowerCase().startsWith(inputValue.toLowerCase());
}
function filterCollection(collection, inputValue, filter, isCreatableOptionVisible) {
    const shouldShowCreatableOption = !isCreatableOptionVisible || isCreatableOptionVisible(inputValue, collection);
    return new ListCollection(filterNodes(collection, inputValue, filter, shouldShowCreatableOption));
}
function filterNodes(nodes, inputValue, filter, shouldShowCreatableOption) {
    let filteredNode = [];
    for (let node of nodes) {
        const isNodeVisible = node.key === CREATE_OPTION_VALUE
            ? shouldShowCreatableOption
            : filter(node.textValue, inputValue);
        if (node.type === 'section' && node.hasChildNodes) {
            let filtered = filterNodes(node.childNodes, inputValue, filter, shouldShowCreatableOption);
            if ([...filtered].length > 0) {
                filteredNode.push({ ...node, childNodes: filtered });
            }
        }
        else if (node.type !== 'section' && isNodeVisible) {
            filteredNode.push({ ...node });
        }
    }
    return filteredNode;
}
function getCollectionWithoutCreateOption(nodes) {
    return new ListCollection(filterNodes(nodes, '', stubTrue, false));
}
